import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, User, ArrowLeft, Shield, X, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { SESSAO_EXPIRADA_KEY } from '../services/api';

const AdminLoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [erroConexao, setErroConexao] = useState(false);
  const [alertModal, setAlertModal] = useState<{
    show: boolean;
    type: 'error' | 'success' | 'warning';
    title: string;
    message: string;
    persistent?: boolean;
  }>({
    show: false,
    type: 'error',
    title: '',
    message: ''
  });
  
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const aviso = sessionStorage.getItem(SESSAO_EXPIRADA_KEY);
    if (aviso) {
      sessionStorage.removeItem(SESSAO_EXPIRADA_KEY);
      toast.error(aviso, { id: 'sessao-expirada', duration: 6000 });
    }
  }, []);

  // Função para mostrar alertas modais
  const showAlert = (type: 'error' | 'success' | 'warning', title: string, message: string, persistent: boolean = false) => {
    setAlertModal({
      show: true,
      type,
      title,
      message,
      persistent
    });
  };

  // Função para fechar alerta
  const closeAlert = () => {
    setAlertModal(prev => ({ ...prev, show: false }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('🔍 [ADMIN-LOGIN-PAGE] Dados do formulário:', { email, senha });
    
    if (!email || !senha) {
      console.log('❌ [ADMIN-LOGIN-PAGE] Campos obrigatórios ausentes:', { email: !!email, senha: !!senha });
      showAlert('warning', 'Campos Obrigatórios', 'Preencha todos os campos para continuar.');
      return;
    }

    // Validação básica (removida validação estrita de email para permitir usernames)
    /* const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      showAlert('warning', 'Email Inválido', 'Por favor, insira um email válido.');
      return;
    } */

    setIsLoading(true);
    setErroConexao(false);
    
    try {
      await login(email, senha, true); // true = login administrativo
      showAlert('success', 'Login Administrativo Realizado!', 'Login realizado com sucesso! Redirecionando para o dashboard...');
      
      // O redirecionamento será feito pelo AuthContext automaticamente
    } catch (error: any) {
      // Tratamento específico de erros baseado no status e mensagem
      let errorTitle = 'Erro no Login';
      let errorMessage = 'Erro ao fazer login';
      let isConnectionError = false;
      let isPersistent = false;
      
      if (error.message) {
        if (error.message.includes('Usuário não encontrado')) {
          errorTitle = 'Usuário Não Encontrado';
          errorMessage = 'Usuário administrativo não encontrado. Verifique o email e tente novamente.';
          isPersistent = true;
        } else if (error.message.includes('Senha incorreta') || error.message.includes('Credenciais inválidas')) {
          errorTitle = 'Credenciais Incorretas';
          errorMessage = 'Email ou senha incorretos. Verifique suas credenciais administrativas e tente novamente.';
          isPersistent = true;
        } else if (error.message.includes('Email e senha são obrigatórios')) {
          errorTitle = 'Campos Obrigatórios';
          errorMessage = 'Por favor, preencha todos os campos.';
        } else if (error.message.includes('Erro interno do servidor')) {
          errorTitle = 'Servidor Indisponível';
          errorMessage = 'Servidor temporariamente indisponível. Tente novamente em alguns minutos.';
          isConnectionError = true;
        } else if (error.message.includes('Sem conexão com o servidor') || 
                   error.message.includes('Network Error') || 
                   error.message.includes('ERR_NETWORK')) {
          errorTitle = 'Sem Conexão';
          errorMessage = 'Sem conexão com a internet. Verifique sua conexão e tente novamente.';
          isConnectionError = true;
          isPersistent = true;
        } else if (error.message.includes('timeout')) {
          errorTitle = 'Tempo Esgotado';
          errorMessage = 'Tempo limite esgotado. Verifique sua conexão e tente novamente.';
          isConnectionError = true;
        } else if (error.message.includes('404')) {
          errorTitle = 'Serviço Não Encontrado';
          errorMessage = 'Serviço não encontrado. Entre em contato com o suporte.';
        } else if (error.message.includes('500')) {
          errorTitle = 'Erro do Servidor';
          errorMessage = 'Erro interno do servidor. Tente novamente mais tarde.';
          isConnectionError = true;
        } else {
          errorMessage = error.message;
        }
      } else if (error.response) {
        // Tratar erros de resposta HTTP
        switch (error.response.status) {
          case 400:
            errorTitle = 'Dados Inválidos';
            errorMessage = 'Dados inválidos. Verifique os campos preenchidos.';
            break;
          case 401:
            errorTitle = 'Credenciais Incorretas';
            errorMessage = 'Email ou senha incorretos. Verifique suas credenciais administrativas e tente novamente.';
            isPersistent = true;
            break;
          case 403:
            errorTitle = 'Acesso Negado';
            errorMessage = 'Acesso negado. Sua conta administrativa pode estar desativada.';
            isPersistent = true;
            break;
          case 404:
            errorTitle = 'Serviço Não Encontrado';
            errorMessage = 'Serviço não encontrado. Entre em contato com o suporte.';
            break;
          case 429:
            errorTitle = 'Muitas Tentativas';
            errorMessage = 'Muitas tentativas de login. Aguarde alguns minutos e tente novamente.';
            isPersistent = true;
            break;
          case 500:
            errorTitle = 'Erro do Servidor';
            errorMessage = 'Erro interno do servidor. Tente novamente mais tarde.';
            isConnectionError = true;
            break;
          case 503:
            errorTitle = 'Serviço Indisponível';
            errorMessage = 'Serviço temporariamente indisponível. Tente novamente em alguns minutos.';
            isConnectionError = true;
            break;
          default:
            errorTitle = 'Erro de Conexão';
            errorMessage = 'Erro de conexão. Verifique sua internet e tente novamente.';
            isConnectionError = true;
        }
      } else if (error.request) {
        // Erro de rede
        errorTitle = 'Sem Conexão';
        errorMessage = 'Sem conexão com o servidor. Verifique sua internet e tente novamente.';
        isConnectionError = true;
        isPersistent = true;
      }
      
      setErroConexao(isConnectionError);
      
      // Mostrar alerta modal para erros importantes
      if (isPersistent || errorTitle === 'Credenciais Incorretas' || errorTitle === 'Acesso Negado' || errorTitle === 'Usuário Não Encontrado') {
        showAlert('error', errorTitle, errorMessage, true);
      } else {
        // Para erros menos críticos, usar toast
        toast.error(errorMessage);
      }
      
      // Limpar apenas a senha em caso de erro, manter o email para facilitar nova tentativa
      setSenha('');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Botão Voltar */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-8"
        >
          <ArrowLeft className="h-5 w-5" />
          Voltar ao Menu
        </Link>

        {/* Card de Login */}
        <div className="bg-gradient-to-br from-gray-800/90 to-gray-900/90 rounded-3xl p-8 border border-gray-700/50 shadow-2xl backdrop-blur-sm">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              Área Administrativa
            </h1>
            <p className="text-gray-400">
              Faça login para acessar o painel de gestão
            </p>
          </div>

          {/* Formulário */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Campo Email */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-gray-700/50 border border-gray-600/30 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                  placeholder="admin@empresa.com"
                  required
                />
              </div>
            </div>

            {/* Campo Senha */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Senha
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type={mostrarSenha ? 'text' : 'password'}
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 bg-gray-700/50 border border-gray-600/30 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                  placeholder="Sua senha"
                  required
                />
                <button
                  type="button"
                  onClick={() => setMostrarSenha(!mostrarSenha)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                >
                  {mostrarSenha ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Indicador de erro de conexão */}
            {erroConexao && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 text-center">
                <p className="text-red-400 text-sm">
                  ⚠️ Problema de conexão detectado. Verifique sua internet.
                </p>
              </div>
            )}

            {/* Botão de Login */}
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-3 rounded-xl font-bold transition-all duration-300 transform shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none ${
                erroConexao 
                  ? 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700' 
                  : 'bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 hover:scale-105'
              } text-white`}
            >
              {isLoading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Entrando...
                </div>
              ) : (
                'Acessar Dashboard'
              )}
            </button>
          </form>

          {/* Separador */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-600/30"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-gray-800/90 text-gray-400">ou</span>
            </div>
          </div>

          {/* Link para Login de Cliente */}
          <div className="text-center">
            <p className="text-gray-400 text-sm mb-3">
              É um cliente? Faça login para comprar
            </p>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors font-medium"
            >
              <User className="h-4 w-4" />
              Login de Cliente
            </Link>
          </div>
        </div>
      </div>

      {/* Modal de Alerta */}
      {alertModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl transform transition-all duration-300 scale-100 animate-in fade-in-0 zoom-in-95">
            {/* Header do Modal */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                {alertModal.type === 'error' && (
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                    <AlertCircle className="w-6 h-6 text-red-600" />
                  </div>
                )}
                {alertModal.type === 'success' && (
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                )}
                {alertModal.type === 'warning' && (
                  <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                    <AlertCircle className="w-6 h-6 text-yellow-600" />
                  </div>
                )}
                <h3 className="text-lg font-bold text-gray-900">
                  {alertModal.title}
                </h3>
              </div>
              {!alertModal.persistent && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    closeAlert();
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              )}
            </div>

            {/* Conteúdo do Modal */}
            <div className="mb-6">
              <p className="text-gray-700 leading-relaxed">
                {alertModal.message}
              </p>
            </div>

            {/* Botões do Modal */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  closeAlert();
                }}
                className={`flex-1 py-3 px-4 rounded-xl font-bold transition-all duration-300 ${
                  alertModal.type === 'error'
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : alertModal.type === 'success'
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-yellow-600 hover:bg-yellow-700 text-white'
                }`}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLoginPage;
